import { AdStatsEx, combineProductsV2, InventoryStats, OrderStats } from "@front/m19-models";

import {
  AD_SALES,
  buildSummableMetric,
  Metric,
  MetricCategory,
  PALETTE,
  PROFIT,
  RatioMetric,
  SummableMetric,
  SupportedAccountType,
  TOTAL_ORDERS,
  TOTAL_SALES,
} from "@front/m19-metrics";

import { AccountType } from "@front/m19-api-client";
import { AggregationFunction, composeAggregationFunctions, sumAdStats, sumOrderStat } from "@front/m19-utils";
import { SumMetric } from "./Metric";

export const VENDOR_SHIPPED_COGS: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "VENDOR_SHIPPED_COGS",
  field: "vendorShippedCogs",
  title: "Shipped Cogs",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[16],
  supportedAccountType: SupportedAccountType.VENDOR,
  currency: true,
  requireSellingPartnerAccess: true,
  tooltip: "Sum of shipped cogs",
  mustApplyEvolutionStyle: false,
});

export const VENDOR_CUSTOMER_RETURNS: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "VENDOR_CUSTOMER_RETURNS",
  field: "vendorCustomerReturns",
  title: "Customer Returns",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[18],
  supportedAccountType: SupportedAccountType.VENDOR,
  currency: false,
  requireSellingPartnerAccess: true,
  tooltip: "Sum of customer returns",
  higherIsBetter: false,
});
export const ORDERS_7D: Metric<InventoryStats> = new SummableMetric<InventoryStats>({
  id: "ORDERS_7D",
  field: "orders7d",
  title: "Orders Last 7 Days",
  category: MetricCategory.INVENTORY,
  color: PALETTE[0],
  supportedAccountType: SupportedAccountType.SELLER,
  titleSmall: "Orders 7d",
  tooltip: "Orders over the last 7 days",
  requireSellingPartnerAccess: true,
});
export const ORDERS_30D: Metric<InventoryStats> = new SummableMetric<InventoryStats>({
  id: "ORDERS_30D",
  field: "orders30d",
  title: "Orders Last 30 Days",
  category: MetricCategory.INVENTORY,
  color: PALETTE[1],
  supportedAccountType: SupportedAccountType.SELLER,
  titleSmall: "Orders 30d",
  tooltip: "Orders over the last 30 days",
  requireSellingPartnerAccess: true,
});
export const FULFILLABLE_STOCK: Metric<InventoryStats> = new SummableMetric<InventoryStats>({
  id: "FULFILLABLE_STOCK",
  field: "fulfillableQuantity",
  title: "FBA Available Inventory",
  category: MetricCategory.INVENTORY,
  color: PALETTE[0],
  supportedAccountType: SupportedAccountType.SELLER,
  titleSmall: "Avail.",
  tooltip: "Available inventory fulfilled by Amazon",
  requireSellingPartnerAccess: true,
  mustApplyEvolutionStyle: false,
});
export const FULFILLABLE_STOCK_VALUE: Metric<InventoryStats> = new SummableMetric<InventoryStats>({
  id: "FULFILLABLE_STOCK_VALUE",
  field: "fulfillableQuantityValue",
  title: "FBA Available Inventory Value",
  category: MetricCategory.INVENTORY,
  color: PALETTE[0],
  currency: true,
  supportedAccountType: SupportedAccountType.SELLER,
  titleSmall: "Avail.",
  tooltip: "Available inventory value fulfilled by Amazon",
  requireSellingPartnerAccess: true,
  mustApplyEvolutionStyle: false,
});

export const FBM_STOCK: Metric<InventoryStats> = new SummableMetric<InventoryStats>({
  id: "FBM_STOCK",
  field: "fbmStock",
  title: "FBM Available Inventory",
  category: MetricCategory.INVENTORY,
  color: PALETTE[0],
  supportedAccountType: SupportedAccountType.SELLER,
  titleSmall: "Avail. FBM",
  tooltip: "Stock available on Amazon and ready to be sold by merchant",
  requireSellingPartnerAccess: true,
  mustApplyEvolutionStyle: false,
});

export const FBM_STOCK_VALUE: Metric<InventoryStats> = new SummableMetric<InventoryStats>({
  id: "FBM_STOCK_VALUE",
  field: "fbmStockValue",
  title: "FBM Available Inventory Value",
  category: MetricCategory.INVENTORY,
  color: PALETTE[0],
  currency: true,
  supportedAccountType: SupportedAccountType.SELLER,
  titleSmall: "Avail. FBM",
  tooltip: "Stock value available on Amazon and ready to be sold by merchant",
  requireSellingPartnerAccess: true,
  mustApplyEvolutionStyle: false,
});

export const INBOUND_STOCK: Metric<InventoryStats> = new SummableMetric<InventoryStats>({
  id: "INBOUND_STOCK",
  field: "inboundQuantity",
  title: "Inbound Stock",
  category: MetricCategory.INVENTORY,
  color: PALETTE[1],
  supportedAccountType: SupportedAccountType.SELLER,
  titleSmall: "In.",
  tooltip: "Inventory shipped but it but has not yet been received",
  requireSellingPartnerAccess: true,
  mustApplyEvolutionStyle: false,
});
export const INBOUND_STOCK_VALUE: Metric<InventoryStats> = new SummableMetric<InventoryStats>({
  id: "INBOUND_STOCK_VALUE",
  field: "inboundQuantityValue",
  title: "Inbound Stock Value",
  category: MetricCategory.INVENTORY,
  color: PALETTE[1],
  currency: true,
  supportedAccountType: SupportedAccountType.SELLER,
  titleSmall: "In.",
  tooltip: "Value of inventory shipped but not yet received",
  requireSellingPartnerAccess: true,
  mustApplyEvolutionStyle: false,
});
export const RESERVED_STOCK: Metric<InventoryStats> = new SummableMetric<InventoryStats>({
  id: "RESERVED_STOCK",
  field: "reservedQuantity",
  title: "Reserved Stock",
  category: MetricCategory.INVENTORY,
  color: PALETTE[2],
  supportedAccountType: SupportedAccountType.SELLER,
  titleSmall: "Res.",
  tooltip: "Inventory already sold but hasn't been shipped to the customer yet",
  requireSellingPartnerAccess: true,
  mustApplyEvolutionStyle: false,
});
export const RESERVED_STOCK_VALUE: Metric<InventoryStats> = new SummableMetric<InventoryStats>({
  id: "RESERVED_STOCK_VALUE",
  field: "reservedQuantityValue",
  title: "Reserved Stock Value",
  category: MetricCategory.INVENTORY,
  color: PALETTE[2],
  currency: true,
  supportedAccountType: SupportedAccountType.SELLER,
  titleSmall: "Res.",
  tooltip: "Value of inventory already sold but hasn't been shipped to the customer yet",
  requireSellingPartnerAccess: true,
  mustApplyEvolutionStyle: false,
});
export const UNFULFILLABLE_STOCK: Metric<InventoryStats> = new SummableMetric<InventoryStats>({
  id: "UNFULLFILLABLE_STOCK",
  field: "unsellableQuantity",
  title: "Unfulfillable Stock",
  category: MetricCategory.INVENTORY,
  color: PALETTE[3],
  supportedAccountType: SupportedAccountType.SELLER,
  titleSmall: "Unful.",
  tooltip: "Damaged or expired items that cannot be sold",
  requireSellingPartnerAccess: true,
  mustApplyEvolutionStyle: false,
});
export const UNFULFILLABLE_STOCK_VALUE: Metric<InventoryStats> = new SummableMetric<InventoryStats>({
  id: "UNFULFILLABLE_STOCK_VALUE",
  field: "unsellableQuantityValue",
  title: "Unfulfillable Stock Value",
  category: MetricCategory.INVENTORY,
  color: PALETTE[3],
  currency: true,
  supportedAccountType: SupportedAccountType.SELLER,
  titleSmall: "Unful.",
  tooltip: "Value of damaged or expired items that cannot be sold",
  requireSellingPartnerAccess: true,
  mustApplyEvolutionStyle: false,
});

export const NET_PPM: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "NET_PPM",
  field: "netPureProductMargin",
  title: "Net PPM",
  category: MetricCategory.HIDDEN,
  color: PALETTE[10],
  supportedAccountType: SupportedAccountType.VENDOR,
  tooltip: "Net Pure Product Margin",
});
export const NET_PURE_PRODUCT_MARGIN: Metric<AdStatsEx> = new RatioMetric<AdStatsEx>({
  id: "NET_PURE_PRODUCT_MARGIN",
  numerator: NET_PPM,
  denominator: undefined,
  title: "Net PPM",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[10],
  graphTension: 1,
  stepped: true,
  keepLastValue: true,
  supportedAccountType: SupportedAccountType.VENDOR,
  requireSellingPartnerAccess: true,
  tooltip: "last Net Pure Product Margin",
  isPercent: true,
  precision: "1.0-0",
});
export const VENDOR_SHIPPED_COGS_RATIO: Metric<AdStatsEx> = new RatioMetric<AdStatsEx>({
  id: "SHIPPED_COGS_RATIO",
  numerator: VENDOR_SHIPPED_COGS,
  denominator: TOTAL_SALES,
  title: "Shipped Cogs Ratio",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[3],
  isPercent: true,
  inverseColors: true,
  tooltip: "Ratio of Shipped Cogs to Shipped Sales",
  mustApplyEvolutionStyle: false,
  supportedAccountType: SupportedAccountType.VENDOR,
});

export const SPONSORED_SALES_SHARE: Metric<AdStatsEx> = new RatioMetric<AdStatsEx>({
  id: "SPONSORED_SALES_SHARE",
  numerator: AD_SALES,
  denominator: TOTAL_SALES,
  title: "Ad Sales Share",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[12],
  isPercent: true,
  precision: "1.0-2",
  titleSmall: "Sp. Sales %",
  tooltip: "Percentage of sales generated through advertising",
  requireSellingPartnerAccess: true,
});

// Traffic metrics
export const WEB_SESSIONS: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "WEB_SESSIONS",
  field: "browserSessions",
  title: "Web Sessions",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[13],
  titleSmall: "Web Sess.",
  requireSellingPartnerAccess: true,
  supportedAccountType: SupportedAccountType.SELLER,
  tooltip: "Number of unique visits to your Amazon page by Web users within a 24-hour period",
});
export const MOBILE_SESSIONS: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "MOBILE_SESSIONS",
  field: "mobileAppSessions",
  title: "Mobile Sessions",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[14],
  titleSmall: "Mobile Sess.",
  requireSellingPartnerAccess: true,
  supportedAccountType: SupportedAccountType.SELLER,
  tooltip: "Number of unique visits to your Amazon page by mobile users within a 24-hour period",
});

export const WEB_PAGE_VIEWS: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "WEB_PAGE_VIEWS",
  field: "browserPageViews",
  title: "Web Page Views",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[15],
  titleSmall: "Web pv.",
  requireSellingPartnerAccess: true,
  supportedAccountType: SupportedAccountType.SELLER,
  tooltip: "Number of times a product detail page is viewed by Web users",
});
export const MOBILE_PAGE_VIEWS: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "MOBILE_PAGE_VIEWS",
  field: "mobileAppPageViews",
  title: "Mobile Page Views",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[16],
  titleSmall: "Mobile pv.",
  requireSellingPartnerAccess: true,
  supportedAccountType: SupportedAccountType.SELLER,
  tooltip: "Number of times a product detail page is viewed by mobile users",
});
export const BUY_BOX_PAGE_VIEW: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "BUY_BOX_PAGE_VIEW",
  field: "buyBoxPageViews",
  title: "Buy Box Page Views",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[17],
  titleSmall: "Bbox pv.",
  requireSellingPartnerAccess: true,
  supportedAccountType: SupportedAccountType.SELLER,
  tooltip: "Number of times a product detail page is viewed with the Buy Box",
});

export const SESSIONS: Metric<AdStatsEx> = new SumMetric<AdStatsEx>({
  id: "SESSIONS",
  metrics: [WEB_SESSIONS, MOBILE_SESSIONS],
  title: "Sessions",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[18],
  titleSmall: "Sess.",
  supportedAccountType: SupportedAccountType.SELLER,
  requireSellingPartnerAccess: true,
  tooltip: "Number of unique visits to your Amazon page by a user within a 24-hour period",
});

export const PAGE_VIEWS: Metric<AdStatsEx> = new SumMetric<AdStatsEx>({
  id: "PAGE_VIEWS",
  metrics: [WEB_PAGE_VIEWS, MOBILE_PAGE_VIEWS],
  title: "Page Views",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[19],
  titleSmall: "pv.",
  requireSellingPartnerAccess: true,
  tooltip: "Number of times a product detail page is viewed",
  supportedAccountType: SupportedAccountType.VENDOR_AND_SELLER,
});

export const APP_RATIO: Metric<AdStatsEx> = new RatioMetric<AdStatsEx>({
  id: "APP_RATIO",
  numerator: MOBILE_SESSIONS,
  denominator: SESSIONS,
  title: "Mobile Shares",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[20],
  isPercent: true,
  precision: "1.0-0",
  titleSmall: "Mobile %",
  supportedAccountType: SupportedAccountType.SELLER,
  requireSellingPartnerAccess: true,
  tooltip: "Share of the total session from mobile",
});

export const BUY_BOX_RATE: Metric<AdStatsEx> = new RatioMetric<AdStatsEx>({
  id: "BUY_BOX_RATE",
  numerator: BUY_BOX_PAGE_VIEW,
  denominator: PAGE_VIEWS,
  title: "Buy Box Win Rate",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[21],
  isPercent: true,
  precision: "1.0-0",
  titleSmall: "Bbox %",
  supportedAccountType: SupportedAccountType.SELLER,
  requireSellingPartnerAccess: true,
  tooltip: "Percentage of time a seller's product listing wins the Buy Box",
});

export const TOTAL_CONVERSION_RATE: Metric<AdStatsEx> = new RatioMetric<AdStatsEx>({
  id: "TOTAL_CONVERSION_RATE",
  numerator: TOTAL_ORDERS,
  denominator: SESSIONS,
  title: "TCR",
  category: MetricCategory.SALES_STATS,
  color: PALETTE[22],
  isPercent: true,
  tooltip: "Total conversion rate = all units sold / sessions",
  supportedAccountType: SupportedAccountType.SELLER,
  requireSellingPartnerAccess: true,
});

export const ASINS_WITH_TOP_OF_SEARCH_RANKINGS_FIRED: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "ASINS_WITH_TOP_OF_SEARCH_RANKINGS_FIRED",
  field: "nbAsinsWithRuleFired",
  title: "ASINs with T.O.S.R. fired",
  category: MetricCategory.OTHER,
  color: "rgba(255, 150, 150, 1)",
  graphTension: 0,
  graphBorderDash: [10, 5],
  currency: false,
  tooltip: "Number of ASINs with Top of Search Rankings fired",
});

// VENDOR INVENTORY METRICS
export const VENDOR_SELLABLE_UNITS: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "SELLABLE_UNITS",
  childField: "inventorySellableUnits",
  field: "vendorInventoryAdStats",
  title: "Sellable Units",
  category: MetricCategory.INVENTORY,
  color: PALETTE[1],
  titleSmall: "Sellable Units",
  tooltip: "Number of sellable units",
  mustApplyEvolutionStyle: false,
  supportedAccountType: SupportedAccountType.VENDOR,
});

export const VENDOR_SELLABLE_COST: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "SELLABLE_COST",
  field: "vendorInventoryAdStats",
  childField: "inventorySellableCost",
  title: "Sellable Cost",
  currency: true,
  category: MetricCategory.INVENTORY,
  color: PALETTE[3],
  titleSmall: "Sellable Cost",
  tooltip: "Sum of inventory sellable cost",
  mustApplyEvolutionStyle: false,
  supportedAccountType: SupportedAccountType.VENDOR,
});
export const VENDOR_UNSELLABLE_UNITS: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "UNSELLABLE_UNITS",
  field: "vendorInventoryAdStats",
  childField: "inventoryUnsellableUnits",
  title: "Unsellable Units",
  category: MetricCategory.INVENTORY,
  color: PALETTE[5],
  titleSmall: "Unsellable Units",
  tooltip: "Number of unsellable units",
  supportedAccountType: SupportedAccountType.VENDOR,
  higherIsBetter: false,
});

export const VENDOR_UNSELLABLE_COST: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "UNSELLABLE_COST",
  field: "vendorInventoryAdStats",
  childField: "inventoryUnsellableCost",
  title: "Unsellable Cost",
  currency: true,
  category: MetricCategory.INVENTORY,
  color: PALETTE[7],
  titleSmall: "Unsellable Cost",
  tooltip: "Sum of unsellable cost",
  supportedAccountType: SupportedAccountType.VENDOR,
  higherIsBetter: false,
});

export const VENDOR_OPEN_PURCHASE_ORDER_UNITS: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "OPEN_PURCHASE_ORDER_UNITS",
  field: "vendorInventoryAdStats",
  childField: "inventoryOpenPurchaseOrderUnits",
  title: "Open Purchase Order Units",
  category: MetricCategory.INVENTORY,
  color: PALETTE[9],
  titleSmall: "Open Purchase Units",
  tooltip: "Number of open purchase order units",
  supportedAccountType: SupportedAccountType.VENDOR,
  mustApplyEvolutionStyle: false,
});

export const VENDOR_AGED90P_SELLABLE_UNITS: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "AGED90P_SELLABLE_UNITS",
  field: "vendorInventoryAdStats",
  childField: "inventoryAged90pSellableUnits",
  title: "Aged 90 Plus Days Sellable Units",
  category: MetricCategory.INVENTORY,
  color: PALETTE[11],
  titleSmall: "Old Sellable Units",
  tooltip: "Number of aged 90 plus days sellable inventory units",
  supportedAccountType: SupportedAccountType.VENDOR,
});

export const VENDOR_AGED90P_SELLABLE_COST: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "AGED90P_SELLABLE_COST",
  field: "vendorInventoryAdStats",
  childField: "inventoryAged90pSellableCost",
  title: "Aged 90 Plus Days Sellable Cost",
  category: MetricCategory.INVENTORY,
  currency: true,
  color: PALETTE[13],
  titleSmall: "Old Sellable Cost",
  tooltip: "Number of aged 90 plus days sellable inventory cost",
  supportedAccountType: SupportedAccountType.VENDOR,
  higherIsBetter: false,
});

// see https://github.com/m19-dev/main-repo/issues/6387
export const VENDOR_OUT_OF_STOCK_VIEWS: Metric<AdStatsEx> = new SummableMetric<AdStatsEx>({
  id: "VENDOR_OUT_OF_STOCK_VIEWS",
  field: "outOfStockGlanceViews",
  title: "Out of Stock Glance Views",
  category: MetricCategory.INVENTORY,
  color: PALETTE[15],
  tooltip:
    "Number of times products are shown as unavailable for purchase on the product detail page, even though they can be sourced from a 1P vendor. Factors that affect this metric include temporary sales suppression due to repeat customer complaints or safety concerns (Andon Cords), manual buyer suppressions, and lack of inventory.",
  supportedAccountType: SupportedAccountType.VENDOR,
  higherIsBetter: false,
});

// see https://github.com/m19-dev/main-repo/issues/6387
export const VENDOR_SOROOS: Metric<AdStatsEx> = new RatioMetric<AdStatsEx>({
  id: "SoROOS",
  numerator: VENDOR_OUT_OF_STOCK_VIEWS,
  denominator: PAGE_VIEWS,
  isPercent: true,
  precision: "1.2-2",
  title: "SoROOS",
  category: MetricCategory.INVENTORY,
  color: PALETTE[16],
  tooltip:
    "Sourceable Replenishment Out of Stock. This metric measures the instances when products are shown as unavailable for purchase on the product detail page, even though they can be sourced from a 1P vendor. Factors that affect SoROOS include temporary sales suppression due to repeat customer complaints or safety concerns (Andon Cords), manual buyer suppressions, and lack of inventory.",
  supportedAccountType: SupportedAccountType.VENDOR,
  higherIsBetter: false,
});

export const ORDER_GLOBAL_FEE: Metric<OrderStats> = buildSummableMetric("ORDER_GLOBAL_FEE", "fee", "Fees", PALETTE[4]);

export const ORDER_GLOBAL_ADV: Metric<OrderStats> = buildSummableMetric(
  "ORDER_GLOBAL_ADV",
  "advertising",
  "Advertising",
  PALETTE[5],
  "Amount spent on advertising on a given period",
  "Adv.",
);

// Sales
export const GROSS_SALES: Metric<OrderStats> = buildSummableMetric(
  "ORDER_SALES", // do not rename id persisted in DB + localStorage + en.json
  "grossSales",
  "Gross Sales",
  PALETTE[0],
);
export const RAW_SALES: Metric<OrderStats> = buildSummableMetric("RAW_SALES", "rawSales", "Raw Sales", PALETTE[2]);

export const REFUNDED_SALES: Metric<OrderStats> = buildSummableMetric(
  "REFUNDED_SALES",
  "refundedSales",
  "Refunded Sales",
  PALETTE[0],
  "Refunds issued to customers",
);

export const FBA_REVERSAL_REIMBURSEMENT: Metric<OrderStats> = buildSummableMetric(
  "FBA_REVERSAL_REIMBURSEMENT",
  "fbaReversalReimbursement",
  "FBA Reversal Reimbursement",
  PALETTE[1],
  "FBA Reversal Reimbursement",
);

export const PROMOTION: Metric<OrderStats> = buildSummableMetric(
  "PROMOTION",
  "promotion",
  "Promotions",
  PALETTE[2],
  "Promotions",
  "Promo.",
  MetricCategory.PROFIT_STATS,
);

// points kind of JP promo
export const POINTS: Metric<OrderStats> = buildSummableMetric("POINTS", "points", "Points", PALETTE[2]);

export const COST_OF_GOODS: Metric<OrderStats> = buildSummableMetric(
  "COST_OF_GOODS",
  "costOfGoods",
  "Cost Of Goods",
  PALETTE[8],
  "Total cost of producing a product",
  undefined,
  MetricCategory.PROFIT_STATS,
);

export const ROI: Metric<OrderStats> = new RatioMetric<OrderStats>({
  id: "ROI",
  numerator: PROFIT,
  denominator: COST_OF_GOODS,
  title: "ROI",
  isPercent: true,
  category: MetricCategory.PROFIT_STATS,
  color: "#7fb3fa",
  coeff: -1,
  supportedAccountType: SupportedAccountType.SELLER,
  tooltip: "Return on investment",
});

export function isMetricSupportedForAccountType(metric: Metric<AdStatsEx>, accountType: AccountType) {
  switch (metric.supportedAccountType) {
    case SupportedAccountType.VENDOR:
      return accountType == AccountType.VENDOR;
    case SupportedAccountType.SELLER:
      return accountType == AccountType.SELLER;
    case SupportedAccountType.VENDOR_AND_SELLER:
      return true;
  }
}

export function groupByCategory<T>(metrics: Metric<T>[]): Map<MetricCategory, Metric<T>[]> {
  const groupedPageMetrics = new Map<MetricCategory, Metric<T>[]>();
  for (const m of metrics) {
    if (!groupedPageMetrics.has(m.category)) {
      groupedPageMetrics.set(m.category, [m]);
    } else {
      groupedPageMetrics.get(m.category)!.push(m);
    }
  }
  for (const k of groupedPageMetrics.keys()) {
    groupedPageMetrics.get(k)!.sort((a, b) => (a.title > b.title ? 1 : a.title === b.title ? 0 : -1));
  }
  return groupedPageMetrics;
}

export function getAggregationFunction(metricTypes: Set<MetricCategory>): AggregationFunction<any>[] {
  const aggFunctions: AggregationFunction<any>[] = [];

  if (metricTypes.has(MetricCategory.PRODUCT)) {
    aggFunctions.push(combineProductsV2);
  }
  if (metricTypes.has(MetricCategory.AD_STATS) || metricTypes.has(MetricCategory.SALES_STATS)) {
    aggFunctions.push(sumAdStats);
  }
  if (metricTypes.has(MetricCategory.PROFIT_STATS)) {
    aggFunctions.push(sumOrderStat);
  }

  return aggFunctions;
}

export function generateAggregationFunction(metricTypes: Set<MetricCategory>) {
  return composeAggregationFunctions(getAggregationFunction(metricTypes));
}
